<template>
  <div
    class="relative z-101 flex max-w-[200px] min-w-[200px] flex-col border-r border-gray-200/30 bg-gray-200/10 p-2.5"
  >
    <RouterLink
      v-for="(item, index) in items"
      :key="index"
      :to="item.to"
      class="mb-0.5 flex h-8 w-full items-center rounded-md px-2.5 text-gray-800 opacity-60 last:mb-0 hover:bg-gray-200/20"
      active-class="opacity-100! bg-gray-200/35! font-medium"
    >
      <component :is="item.icon" class="mr-2.5 size-3.5" />
      <span class="text-[14px] leading-[20px]">{{ item.label }}</span>
    </RouterLink>

    <div
      v-if="showCallout"
      class="mt-auto p-2.5 pt-[7px] border rounded-md border-green-700/25 bg-green-200/50"
    >
      <div
        class="font-secondary font-medium text-[13px] leading-[18px] text-gray-500 mb-2"
      >
        <template v-if="noFilesAdded">
          Get more from your copyrights. Go Pro
        </template>
        <template v-if="!noFilesAdded">
          <div class="font-semibold text-gray-800">
            <template v-if="fileLimitReached">Registration limit</template>
            <template v-else>1 of 2 files added</template>
          </div>
          <div>
            <template v-if="fileLimitReached">
              Upgrade to register more work.
            </template>
            <template v-else>
              Upgrade to add
              <template v-if="!isBundlePlan">unlimited</template>
              <template v-else>more</template> files
            </template>
          </div>
        </template>
      </div>
      <Button class="w-full" variant="primary" @click="handleUpgradeClick">
        Upgrade
      </Button>
    </div>

    <div
      v-else-if="billingStore.billing?.upsell"
      class="bg-green-200/25 mt-auto p-2.5 border rounded-md border-green-700/25"
    >
      <Pill class="mb-1.5" variant="success">🎉 Limited time </Pill>
      <div
        class="font-secondary font-medium text-[13px] leading-[18px] text-gray-500 mb-2"
      >
        {{ format(billingStore.billing?.upsell?.text) }}
      </div>
      <Button class="w-full" variant="primary" @click="handleClick">
        {{ billingStore.billing?.upsell?.ctaText }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/shared/stores/user'
import { Button, Pill } from '@dacr/library'
import { HomeIcon, ArchivedIcon, FileDocketIcon } from '@dacr/icons'
import { computed, type Component } from 'vue'
import { useRouter } from 'vue-router'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { useBillingStore } from '@/features/billing/store'
import {
  PlanType,
  usePlansQueryQuery,
  UserPermission,
} from '@/shared/graphql/legacy/generated'
import { SelectPlanType } from '@/shared/types/index'
import { analytics } from '@/shared/services/analytics'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'

interface Item {
  label: string
  icon: Component
  to: string
}

const { result } = usePlansQueryQuery()

const router = useRouter()
const userStore = useUserStore()
const billingStore = useBillingStore()
const aiEnabled = useFeatureFlag(FeatureFlag.AI)

const items = computed<Item[]>(() => [
  {
    label: 'Home',
    icon: HomeIcon,
    to: '/home',
  },
  {
    label: 'Archived',
    icon: ArchivedIcon,
    to: '/archive',
  },
  ...(aiEnabled.value
    ? [
        {
          label: 'Infringement Docket',
          icon: FileDocketIcon,
          to: '/infringement',
        },
      ]
    : []),
])

const isBasic = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const isBundlePlan = computed(
  () => billingStore.billing?.plan.type === PlanType.Bundle
)

const noFilesAdded = computed(() => !userStore.user?.totalFiles)

const fileLimitReached = computed(
  () => userStore.user?.totalFiles === userStore.user?.totalFilesLimit
)

const showCallout = computed(
  () =>
    userStore.user &&
    (isBasic.value || (isBundlePlan.value && fileLimitReached.value))
)

const handleUpgradeClick = () => {
  if (fileLimitReached.value && isBasic.value) {
    return openModal(Modals.UpgradeToPro, {
      props: {
        hasReachedLimit: true,
      },
    })
  }

  if (fileLimitReached.value && isBundlePlan.value) {
    return openModal(Modals.UpgradeBundleLimit, {
      props: {
        hasReachedLimit: true,
      },
    })
  }

  openModal(Modals.UpgradeToPro)
}

const format = (text?: string) => {
  if (!text) return

  return text.replace(
    '<price>',
    billingStore.billing?.upsell?.planItem.prices?.[0]?.details
      ?.renderedPrice || ''
  )
}

const handleClick = () => {
  if (
    !billingStore.billing?.upsell?.plan ||
    !billingStore.billing.upsell.planItem
  )
    return

  analytics.track('upsell_cta_clicked', {
    amount:
      (billingStore.billing.upsell.planItem.prices?.[0]?.details?.amount || 0) /
      100,
    currency:
      billingStore.billing.upsell.planItem.prices?.[0]?.details?.currency || '',
  })

  const billingCycle =
    billingStore.billing.upsell.planItem.prices[0].details?.billingCycleType ||
    'month'

  billingStore.setPlanData({
    plan: billingStore.billing?.upsell?.plan,
    planItem: billingStore.billing?.upsell?.planItem,
    billingCycle: {
      type: billingCycle,
      description:
        result.value?.plans?.groupByBillingCycle.find(
          (cycle) => cycle?.billingCycleType === billingCycle
        )?.billingCycleDescription || '',
    },
  })

  router.push(`/${SelectPlanType.Change}/checkout`)
}
</script>
